const TYPE_LABELS = {
  subscription: {
    TRIAL: '체험중',
    ACTIVE: '이용중',
    GRACE_PERIOD: '결제 유예',
    ON_HOLD: '결제 보류',
    PAUSED: '일시정지',
    SUSPENDED: '정지',
    EXPIRED: '만료',
    SETUP_REQUIRED: '설정 필요',
  },
  order: {
    CREATED: '주문생성',
    PENDING: '대기',
    PAYMENT_PENDING: '결제대기',
    PAID: '결제완료',
    FAILED: '실패',
    CANCELED: '취소됨',
    REFUNDED: '환불완료',
    PARTIAL_REFUNDED: '부분환불',
  },
  payment: {
    CREATED: '생성됨',
    PENDING: '대기',
    PAYMENT_PENDING: '결제대기',
    PAID: '결제완료',
    FAILED: '실패',
    CANCELED: '취소됨',
    REFUNDED: '환불완료',
    PARTIAL_REFUNDED: '부분환불',
  },
  refund: {
    REQUESTED: '환불요청',
    APPROVED: '승인됨',
    REJECTED: '반려됨',
    COMPLETED: '처리완료',
    STORE_ACTION_REQUIRED: '스토어 처리 필요',
  },
  document: {
    REQUESTED: '발행요청',
    ISSUED: '발행완료',
    FAILED: '발행실패',
  },
  trial: {
    ACTIVE: '활성',
    REQUESTED: '요청됨',
    REVOKED: '회수됨',
    EXPIRED: '종료됨',
  },
  toggle: {
    ON: '사용',
    OFF: '미사용',
  },
  quota: {
    NONE: '미제공',
    LIMITED: '제한형',
    UNLIMITED: '무제한',
  },
}

const COMMON_LABELS = {
  UNKNOWN: '확인필요',
  PROCESSED: '처리됨',
}

/**
 * @param {string} value
 * @param {'subscription'|'order'|'payment'|'refund'|'document'|'trial'|'toggle'|'quota'|'default'} type
 * @param {{ showCode?: boolean }} options — 관리자 디버깅용으로만 코드를 붙일 때
 */
export function formatBillingStatusLabel(value, type = 'default', options = {}) {
  if (!value) return '-'
  const raw = String(value)
  const normalized = raw.toUpperCase()
  const pick = (ty) => TYPE_LABELS[ty]?.[normalized]
  let label
  if (type && type !== 'default') {
    label = pick(type) || COMMON_LABELS[normalized]
  } else {
    label =
      pick('subscription') ||
      pick('order') ||
      pick('payment') ||
      pick('refund') ||
      pick('trial') ||
      pick('quota') ||
      COMMON_LABELS[normalized]
  }
  if (label) {
    return options.showCode ? `${label} (${raw})` : label
  }
  return '미분류'
}

export function billingStatusToneClass(value, type = 'default') {
  const normalized = String(value || '').toUpperCase()
  if (['PAID', 'ACTIVE', 'COMPLETED', 'ISSUED', 'PROCESSED', 'ON'].includes(normalized)) {
    return 'bg-emerald-100 text-emerald-700'
  }
  if (
    [
      'TRIAL',
      'PAYMENT_PENDING',
      'APPROVED',
      'PARTIAL_REFUNDED',
      'REQUESTED',
      'PENDING',
      'CREATED',
      'UNKNOWN',
      'LIMITED',
      'SETUP_REQUIRED',
      'GRACE_PERIOD',
      'ON_HOLD',
      'PAUSED',
      'STORE_ACTION_REQUIRED',
    ].includes(normalized)
  ) {
    return 'bg-amber-100 text-amber-700'
  }
  if (['FAILED', 'REJECTED', 'CANCELED', 'REFUNDED', 'EXPIRED', 'SUSPENDED', 'OFF', 'REVOKED', 'NONE'].includes(normalized)) {
    return 'bg-red-100 text-red-700'
  }
  if (['UNLIMITED'].includes(normalized)) {
    return 'bg-sky-100 text-sky-700'
  }
  if (type === 'event' || type === 'document') {
    return 'bg-sky-100 text-sky-700'
  }
  return 'bg-slate-100 text-slate-700'
}

export function formatBillingModeLabel(value) {
  if (!value) return '-'
  const normalized = String(value).toUpperCase()
  if (normalized === 'ORG_PAYS') return '조직 결제'
  if (normalized === 'MEMBER_PAYS_WITH_ORG_DISCOUNT') return '조직 할인 개인 결제'
  if (normalized === 'MEMBER_PAYS') return '멤버 결제'
  if (normalized === 'USER_PAYS' || normalized === 'INDIVIDUAL') return '개인 결제'
  if (normalized === 'STORE_IAP') return '인앱결제'
  if (normalized === 'WEB_PG') return '웹 결제'
  if (normalized === 'TRIAL') return '체험'
  return '미분류'
}

export function formatEntitlementSourceLabel(source, fallbackLabel) {
  const normalized = String(source || '').trim().toUpperCase()
  const labels = {
    ORG_CONTRACT: '조직계약',
    STORE_IAP: '인앱결제',
    WEB_PG: '웹결제',
    TRIAL: '체험',
    INDIVIDUAL: '개인구독',
    MISSING: '미생성',
    UNKNOWN: '기타',
  }
  if (labels[normalized]) return labels[normalized]

  const fallback = String(fallbackLabel || '').trim()
  const fallbackLabels = {
    '議곗쭅怨꾩빟': '조직계약',
    '泥댄뿕': '체험',
    '媛쒖씤援щ룆': '개인구독',
    '湲고?': '기타',
  }
  if (fallbackLabels[fallback]) return fallbackLabels[fallback]
  return fallback || '-'
}

export function formatBillingProviderLabel(value) {
  if (!value) return '-'
  const normalized = String(value).trim().toLowerCase()
  const labels = {
    apple_iap: 'App Store',
    google_play: 'Google Play',
    mock: '테스트 결제',
    mock_pg: '테스트 결제',
    pending: '결제 준비 중',
    portone: '포트원',
    portone_pg: '포트원',
    toss: '토스페이먼츠',
    tosspayments: '토스페이먼츠',
    toss_payments: '토스페이먼츠',
  }
  return labels[normalized] || '외부 결제'
}

export function formatBillingDocumentTypeLabel(value) {
  if (!value) return '-'
  const normalized = String(value).trim().toUpperCase()
  const labels = {
    CASH_RECEIPT: '현금영수증',
    INVOICE: '청구서',
    RECEIPT: '영수증',
    TAX_INVOICE: '세금계산서',
  }
  return labels[normalized] || '증빙 문서'
}

export function formatPaymentMethodLabel(value) {
  if (!value) return '-'
  const normalized = String(value).trim().toUpperCase()
  const labels = {
    APPLE_IAP: 'App Store',
    CARD: '카드',
    GOOGLE_PLAY: 'Google Play',
    POINT: '포인트',
    TRANSFER: '계좌이체',
    VBANK: '가상계좌',
  }
  return labels[normalized] || '외부 결제'
}

export function formatQuotaModeLabel(value) {
  return formatBillingStatusLabel(value, 'quota')
}

export function formatRefundActionLabel(action) {
  const normalized = String(action || '').toLowerCase()
  if (normalized === 'approve') return '승인'
  if (normalized === 'reject') return '반려'
  if (normalized === 'complete') return '완료'
  return String(action || '')
}

export function formatBillingAuditActionLabel(action) {
  const normalized = String(action || '').toUpperCase()
  const map = {
    ORDER_CREATED: '주문 생성',
    PAYMENT_CONFIRMED: '결제 승인',
    PAYMENT_CONFIRM_FAILED: '결제 승인 실패',
    WEBHOOK_RECEIVED: '웹훅 수신',
    WEBHOOK_PROCESSED: '웹훅 처리',
    WEBHOOK_DUPLICATED: '웹훅 중복',
    REFUND_REQUESTED: '환불 요청',
    REFUND_COMPLETED: '환불 완료',
    SUBSCRIPTION_ACTIVATED: '구독 활성화',
    SUBSCRIPTION_UPDATE: '구독 수정',
    SUBSCRIPTION_TRIAL_GRANT: 'Trial 지급',
    SUBSCRIPTION_TRIAL_REVOKE: 'Trial 회수',
    SUBSCRIPTION_GRANT_DAYS: '구독일 부여',
    ORDER_RECONCILE: '주문 재검증',
    PAYMENT_CONFIRM: '결제 확정',
    REFUND_REQUEST: '환불 요청',
    환불_승인: '환불 승인',
    환불_반려: '환불 반려',
    환불_완료: '환불 완료',
    PROMOTION_CREATE: '프로모션 생성',
    PROMOTION_UPDATE: '프로모션 수정',
    PROMOTION_STATUS_CHANGE: '프로모션 상태 변경',
    POINT_EXPIRE: '포인트 만료',
    POINT_SPEND: '포인트 차감',
    BILLING_BOOTSTRAP: 'billing 생성',
    ORG_CONTRACT_CREATED: '조직 계약 등록',
    ORG_CONTRACT_UPDATED: '조직 계약 수정',
    ORG_CONTRACT_TERMINATED: '조직 계약 종료',
    QUOTA_CONSUME_ADMIN: '할당량 수동 소모',
    QUOTA_LIMIT_ADJUST_ADMIN: '할당량 한도 조정',
  }
  return map[normalized] || '미분류'
}

export function formatBillingAuditTargetLabel(targetType) {
  const normalized = String(targetType || '').toUpperCase()
  const map = {
    BILLING_ORDER: '주문',
    BILLING_PAYMENT: '결제',
    BILLING_SUBSCRIPTION: '구독',
    BILLING_WEBHOOK_EVENT: '웹훅',
    REFUND: '환불',
    SUBSCRIPTION: '구독',
    ORDER: '주문',
    PAYMENT: '결제',
    PROMOTION: '프로모션',
    POINT: '포인트',
    BILLING_ACCOUNT: '과금 계정',
    PLAN: '플랜',
    PLAN_VERSION: '플랜 버전',
    QUOTA: '할당량',
    TRIAL: 'Trial',
    ORG_BILLING_CONTRACT: '조직 계약',
  }
  return map[normalized] || '미분류'
}

export function formatPromotionRewardTypeLabel(rewardType) {
  const normalized = String(rewardType || '').toUpperCase()
  const map = {
    POINT_GRANT: '포인트 지급',
    TRIAL_START: 'Trial 시작',
    TRIAL_DAYS_EXTEND: 'Trial 기간 연장',
    SUBSCRIPTION_DAYS_EXTEND: '구독 기간 연장',
    AMOUNT_DISCOUNT: '정액 할인',
    PERCENT_DISCOUNT: '정률 할인',
  }
  return map[normalized] || '미분류'
}

export function formatPromotionRewardTargetRoleLabel(targetRole) {
  const normalized = String(targetRole || '').toUpperCase()
  const map = {
    REDEEMER: '사용자',
    REFERRER: '추천인',
    REFEREE: '피추천인',
  }
  return map[normalized] || '미분류'
}
